// Unified order model — mirrors backend/order_status.py.
// TWO independent concepts:
//   • ORDER STAGE  -> `status`          (fulfilment workflow)
//   • PAYMENT      -> `payment_status`  (money — source of truth)

// ---------- ORDER STAGE ----------
export const STATUS_LABELS = {
  checkout_complete: 'Order Placed',
  under_review: 'Ordered from Supplier',
  confirmed: 'Confirmed',
  packing: 'Packing',
  ready_for_collection: 'Ready for Collection',
  closed: 'Closed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  expired: 'Expired',
};

export const STATUS_COLORS = {
  checkout_complete: 'bg-blue-900/30 text-blue-300 border-blue-700/40',
  under_review: 'bg-amber-900/30 text-amber-300 border-amber-700/40',
  confirmed: 'bg-teal/15 text-teal border-teal/30',
  packing: 'bg-indigo-900/30 text-indigo-300 border-indigo-700/40',
  ready_for_collection: 'bg-purple-900/30 text-purple-300 border-purple-700/40',
  closed: 'bg-green-900/30 text-green-300 border-green-700/40',
  cancelled: 'bg-red-900/30 text-red-400 border-red-700/40',
  rejected: 'bg-red-900/30 text-red-400 border-red-700/40',
  expired: 'bg-orange-900/30 text-orange-300 border-orange-700/40',
};

// Legacy order-status -> new order stage (read-side safety).
const LEGACY = {
  submitted: 'checkout_complete',
  pending: 'checkout_complete',
  processing: 'under_review',
  reviewed: 'under_review',
  pending_confirmation: 'under_review',
  pending_payment: 'under_review',
  invoice_sent: 'under_review',
  payment_required: 'under_review',
  payment_received: 'under_review',
  payment_failed: 'under_review',
  paid: 'under_review',
  collected: 'closed',
  collection_notification_sent: 'ready_for_collection',
};

export const normalizeStatus = (s) => (STATUS_LABELS[s] ? s : (LEGACY[s] || s));
export const statusLabel = (s) => STATUS_LABELS[normalizeStatus(s)] || s;
export const statusColor = (s) =>
  STATUS_COLORS[normalizeStatus(s)] || 'bg-muted text-foreground/70 border-border';

// Admin per-row order-stage dropdown.
export const PIPELINE_STATUSES = [
  'checkout_complete', 'under_review', 'confirmed', 'packing',
  'ready_for_collection', 'closed',
  'cancelled', 'rejected', 'expired',
];

// Bulk order-stage targets. Ordered as: back-to-start, forward pipeline, then
// the destructive terminal stages (which warn before applying).
export const BULK_TARGETS = [
  'checkout_complete', 'under_review', 'confirmed', 'packing', 'ready_for_collection', 'closed',
  'cancelled', 'rejected',
];

// Destructive bulk targets — notify customers and may issue refunds/credit.
export const BULK_DESTRUCTIVE = ['cancelled', 'rejected'];

// Stages an order can still be cancelled from (mirrors backend).
export const CANCELLABLE = ['checkout_complete', 'under_review'];

// Terminal stages.
export const CLOSED_STATUSES = ['closed', 'cancelled', 'rejected', 'expired'];

// ---------- PAYMENT ----------
export const PAYMENT_LABELS = {
  unpaid: 'Unpaid',
  payment_requested: 'Payment Requested',
  paid: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
  expired: 'Expired',
};

export const PAYMENT_COLORS = {
  unpaid: 'bg-red-900/25 text-red-300 border-red-700/40',
  payment_requested: 'bg-gold/15 text-gold border-gold/30',
  paid: 'bg-teal/15 text-teal border-teal/30',
  failed: 'bg-red-900/30 text-red-400 border-red-700/40',
  refunded: 'bg-slate-700/30 text-slate-300 border-slate-600/40',
  expired: 'bg-orange-900/30 text-orange-300 border-orange-700/40',
};

export const PAYMENT_OPTIONS = ['unpaid', 'payment_requested', 'paid', 'failed', 'refunded', 'expired'];

export const normalizePayment = (p) => (PAYMENT_LABELS[p] ? p : 'unpaid');
export const paymentLabel = (p) => PAYMENT_LABELS[normalizePayment(p)] || 'Unpaid';
export const paymentColor = (p) =>
  PAYMENT_COLORS[normalizePayment(p)] || 'bg-muted text-foreground/70 border-border';

// Payment states still owing (drives "Pay Now" + reminders).
export const OWING_PAYMENT = ['unpaid', 'payment_requested', 'failed'];
